import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectInvalidEmailsFromState } from 'src/app/CreateVirtualCardForm/redux/createVirtualCardForm.selectors'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import CreditCardAndCsvBox from 'src/components/CreditCardAndCsvBox/CreditCardAndCsvBox'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import ErrorList from 'src/components/ErrorList/ErrorList'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SoftAlertBox from 'src/components/SoftAlertBox/SoftAlertBox'
import SvgCardLoD from 'src/components/Svgs/SvgCardLoD'
import { BillPaySanctionsScreeningNote } from 'src/components/VirtualCardForm/VirtualCardForm'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import {
  VirtualCardPushTaskListModel,
  VirtualCardPushTaskStatus,
} from 'src/models/virtualCardPushTask.models'
import { billPayUrl, virtualCardsUrl } from 'src/navigation/urls'
import {
  issuerIntegratorQueries,
  toIssuerIntegratorListModel,
} from 'src/queries/issuers/issuerIntegrators/issuerIntegrators.queries'
import { selectNullSafeCurrencyFromState } from 'src/redux/selectors/auth.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import {
  selectCardApprovalsEnabled,
  selectCardApprovalsFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { fileIsEmpty } from 'src/utils/validation/file.validation'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

interface MultipleVirtualCardsFormProps {
  className?: string
  labelCreditCardsAsAccounts: boolean
  creditCardOptions: CreditCardOption[]
  errorDetailMap?: ErrorDetailMap<string>
  getCreditCardBulkTemplate: (type: string) => void
  initialFlatFields?: Partial<MultipleVirtualCardsFormFlatFields>
  onSuccessfulSubmit: (flatFields: MultipleVirtualCardsFormFlatFields) => void
  postingCreditCardBulkVirtualCards: boolean
  topLevelError?: string
  virtualCardPushTasks?: VirtualCardPushTaskListModel[]
  creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps
  userHasOrg?: boolean
  userHasActiveRCNs?: boolean
  isBillPay?: boolean
}

export type MultipleVirtualCardsFormFlatFields = {
  creditCardId?: string
  csv?: File
  type?: string
}

const MultipleVirtualCardsForm: React.FC<MultipleVirtualCardsFormProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const defaultCurrency = useSelector(selectNullSafeCurrencyFromState)
  const creditCards = useSelector(selectCreditCardListModelsFromState)
  const invalidEmails = useSelector(selectInvalidEmailsFromState)
  const user = useCurrentUser()
  const cardApprovalsEnabled = useSelector(selectCardApprovalsEnabled)
  const cardApprovalRules = useSelector(selectCardApprovalsFromState)

  const showCardApprovalSoftAlert =
    cardApprovalsEnabled && cardApprovalRules?.approverId !== user?.id

  const {
    fields: flatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
  } = useFormFields<MultipleVirtualCardsFormFlatFields>(
    {
      ...props.initialFlatFields,
    },
    (): FormFieldsValidation<MultipleVirtualCardsFormFlatFields> => {
      return {
        creditCardId: [stringIsEmpty()],
        csv: [fileIsEmpty],
      }
    },
    props.errorDetailMap,
    undefined,
    {
      creditCardId: 0,
      csv: 0,
    },
  )

  const downloadTemplate = (type: string): void => {
    props.getCreditCardBulkTemplate(type)
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    const flatFieldsPassedValidation = flatFieldsSetErrors()
    if (flatFieldsPassedValidation) {
      props.onSuccessfulSubmit(flatFields)
    }
  }

  useEffect(() => {
    if (props.creditCardOptions.length === 1) {
      onFlatFieldChange('creditCardId', props.creditCardOptions[0].value)
    }
  }, [props.creditCardOptions.length])

  const [showTaskTable, setShowTaskTable] = useState(false)
  useEffect((): void => {
    if (
      props.virtualCardPushTasks !== undefined &&
      props.virtualCardPushTasks.length > 0
    ) {
      setShowTaskTable(true)
    }
  }, [props.virtualCardPushTasks])

  const selectedCreditCard: CreditCardListModel | undefined =
    flatFields?.creditCardId !== undefined
      ? creditCards[flatFields.creditCardId]
      : props.creditCardOptions.length === 1
        ? creditCards[props.creditCardOptions[0].value]
        : undefined

  const { data: selectedIssuer } = useQuery({
    ...issuerIntegratorQueries.detail(selectedCreditCard?.issuerId),
    select: toIssuerIntegratorListModel,
  })

  const processingCSV = props.virtualCardPushTasks?.some(
    (task) => task.status === VirtualCardPushTaskStatus.Initiated,
  )
  const allFailed =
    props.virtualCardPushTasks !== undefined &&
    props.virtualCardPushTasks.length ===
      props.virtualCardPushTasks.filter(
        (vc) => vc.status === VirtualCardPushTaskStatus.Failed,
      ).length

  return (
    <form className={props.className} onSubmit={onSubmit}>
      <CreditCardAndCsvBox
        id={'CreditCardAndCsvBox'}
        labelCreditCardsAsAccounts={props.labelCreditCardsAsAccounts}
        creditCardOptions={props.creditCardOptions}
        creditCardIdProps={{
          value: flatFields.creditCardId,
          errors: flatFieldsErrors.creditCardId,
          onChange: (val: string): void => {
            onFlatFieldChange('creditCardId', val)
          },
        }}
        csvProps={{
          value: flatFields.csv,
          errors: flatFieldsErrors.csv,
          onChange: (val: File): void => {
            onFlatFieldChange('csv', val)
          },
        }}
        downloadTemplate={downloadTemplate}
        showTaskTable={showTaskTable}
        virtualCardPushTasks={props.virtualCardPushTasks}
        currency={defaultCurrency}
        creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
        isBillPay={props.isBillPay}
      />
      {props.topLevelError !== undefined && (
        <ErrorList
          css={css`
            margin-top: 10px;
          `}
          errors={[props.topLevelError]}
        />
      )}
      {(invalidEmails?.length ?? 0) > 0 && (
        <ErrorList
          css={css`
            margin-top: 10px;
          `}
          errors={[
            <React.Fragment key={'error'}>
              {translate(
                props.isBillPay
                  ? 'The following users did not receive cards because Bill Pay cards cannot be sent to organization members: ${emails}'
                  : 'The following users did not receive cards because they are not in your org: ${emails}',
                {
                  emails:
                    '\n\t• ' +
                    Array.from(new Set(invalidEmails)).join('\n\t• '),
                },
              )}
            </React.Fragment>,
          ]}
        />
      )}
      {props.isBillPay && (
        <>
          {showCardApprovalSoftAlert && (
            <SoftAlertBox
              css={css`
                border-radius: 6px;
                margin-top: 20px;
                padding: 16px 20px;
                background-color: #fff4cf;
                ${getFontCss(
                  12,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
              testId={'cardApproverSoftAlertBox'}
            >
              {translate(
                "If these cards are above the organization's threshold amount, they will require final approval from the card approver",
              )}
            </SoftAlertBox>
          )}
          {selectedIssuer?.recipientScreeningEnabled && ( // if selected account has screening
            <BillPaySanctionsScreeningNote />
          )}
        </>
      )}
      {!showTaskTable ? (
        <Button
          testId={'createVCBtn'}
          css={css`
            ${(invalidEmails?.length ?? 0) > 0
              ? css`
                  margin-top: 10px;
                `
              : css`
                  margin-top: 40px;
                `}

            ${getBreakpointDown(breakpointSizes.XSM)(css`
              margin-top: 30px;
              width: 100%;
            `)}
          `}
          design={ButtonDesigns.designCta}
          type={ButtonTypes.submit}
          big={true}
          loadingText={translate('Creating...')}
          text={translate('Create Virtual Cards')}
          image={
            <SvgCardLoD
              css={css`
                transform: scale(calc(18 / 24));
                transform-origin: right;
              `}
            />
          }
          loading={props.postingCreditCardBulkVirtualCards}
          disabled={props.postingCreditCardBulkVirtualCards}
        />
      ) : (
        <div
          css={css`
            display: flex;
            flex-direction: row;
            gap: 20px;
            align-items: center;

            ${(invalidEmails?.length ?? 0) > 0
              ? css`
                  margin-top: 10px;
                `
              : css`
                  margin-top: 40px;
                `}

            ${getBreakpointDown(breakpointSizes.XSM)(css`
              margin-top: 30px;
              width: 100%;
              flex-direction: column;
            `)}
          `}
        >
          <Button
            testId={'viewCards'}
            design={ButtonDesigns.designCta}
            type={ButtonTypes.button}
            big={true}
            text={translate(
              props.isBillPay ? 'View Bill Pay Cards' : 'View Cards',
            )}
            loadingText={translate('Upload in Progress')}
            loading={processingCSV}
            disabled={allFailed}
            css={css`
              ${getBreakpointDown(breakpointSizes.XSM)(css`
                width: 100%;
              `)}
            `}
            linkProps={{
              to: props.isBillPay ? billPayUrl() : virtualCardsUrl(),
              ariaLabelSource: props.isBillPay
                ? 'View Bill Pay Cards'
                : 'View Cards',
            }}
          />
          {!processingCSV && (
            <HoverableLink
              ariaLabelSource={'Submit Another CSV'}
              testId={'submitAnotherCSVBtn'}
              onClick={(): void => {
                setShowTaskTable(false)
              }}
            >
              {translate('Submit Another CSV')}
            </HoverableLink>
          )}
        </div>
      )}
    </form>
  )
}

export default MultipleVirtualCardsForm
